'use client';

import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useIntl } from 'react-intl';

import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAuth } from '@/context/auth/AuthProvider';
import messages from '@/messages/messages';
import { CalmAuthMethods } from '@/utils/privacyCookies';

import { useSisu } from '../Provider';
import { type MessageNode } from '../utils';
import { FBIcon, Wrapper } from './styles';

export type FacebookAuthButtonProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-facebook-auth-button"
	 * ```
	 */
	'data-testid'?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * false
	 * ```
	 */
	hideMessage?: boolean;

	message?: MessageNode;
};

export default function FacebookAuthButton({
	'data-testid': testId = 'sisu-facebook-auth-button',
	hideMessage = false,
	message,
}: FacebookAuthButtonProps) {
	const { authenticate } = useAuth();
	const { formatMessage } = useIntl();
	const { authPrompt } = useAuthPrompt();
	const { isMarketingOptInSelected } = useSisu();

	const handleClick = () => {
		authenticate({
			authMethod: CalmAuthMethods.Facebook,
			marketingOptIn: isMarketingOptInSelected,
		});
	};

	if (!message) {
		message = formatMessage(
			authPrompt === 'login' ? messages.facebookSignInMessage : messages.continueFacebookSignupMessage,
		);
	}

	return (
		<Wrapper
			backgroundColor="white"
			contentAlignment="center"
			data-testid={testId}
			onPress={handleClick}
			role="link"
			$noPadding={hideMessage}
			textColor="black"
			borderColor="gray3"
		>
			<FBIcon $noMargin={hideMessage} $iconColor="facebookBlue" />
			{hideMessage ? <VisuallyHidden>{message}</VisuallyHidden> : message}
		</Wrapper>
	);
}
